import React, { FC } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  SystemStyleObject,
} from "@chakra-ui/react";
import { Flex } from "components";
import { FiArrowLeft } from "react-icons/fi";
import { theme } from "@ovou-inc/ovou-ui";

interface DrawerComponentProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footerContent?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  maxWidth?: string;
  showOverlay?: boolean;
  /**
   * Forwarded to Chakra's Drawer. Set false when a modal opens on top of this
   * drawer: the drawer's scroll-lock (react-remove-scroll) otherwise treats the
   * portaled modal as outside its content and cancels wheel events over it.
   */
  blockScrollOnMount?: boolean;
}

const baseStyles: SystemStyleObject = {
  width: "100%",
  p: 4,
  alignItems: "center",
  position: "sticky",
  top: 0,
  zIndex: 999,
};

const DrawerComponent: FC<DrawerComponentProps> = ({
  isOpen,
  onClose,
  children,
  footerContent,
  title,
  size = "md",
  showCloseButton = false,
  maxWidth = "36rem",
  showOverlay = true,
  blockScrollOnMount = true,
}) => {
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      trapFocus={false}
      blockScrollOnMount={blockScrollOnMount}
    >
      <DrawerOverlay
        onClick={onClose}
        background={
          showOverlay !== undefined && !showOverlay ? "transparent" : undefined
        }
      />

      <DrawerContent maxWidth={maxWidth}>
        {showCloseButton && (
          <Flex sx={{ ...baseStyles }} onClick={onClose}>
            <FiArrowLeft
              cursor="pointer"
              size="24"
              color={theme.palette.ui.greys.grey2}
              role="button"
              aria-label="Back"
            />
          </Flex>
        )}
        {title && <DrawerHeader>{title}</DrawerHeader>}

        <DrawerBody padding="0px" sx={{ position: "relative" }}>
          {children}
        </DrawerBody>
        {footerContent && <DrawerFooter>{footerContent}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerComponent;
